import "systemjs/dist/system.min.js";
import { createApp, watch } from "vue";
import * as singleSpa from "single-spa";
import App from "@/shell/App.vue";
import { pinia } from "@/plugins/pinia";
import { bindI18nToHost, i18n } from "@/plugins/i18n";
import { createShellVuetify } from "@/plugins/vuetify";
import { applyTenantDefaultTheme, installGlobalState } from "@/state/globalState";
import { applyBranding } from "@/tenant/branding";
import { API_BASE_URL, fetchTenantApp, resolveTenantHost } from "@/tenant/resolve";
import { paintBootSplash, readSplash, rememberSplash } from "@/tenant/splash";
import { applyDevImportMapOverrides, injectImportMap, registerTenantModules } from "@/spa/registry";
import { decideHomeRedirect, decideRouteGuard } from "@/nav/routeGuard";
import { routeUnderPrefix } from "@/nav/navFilter";
import { isDevHost, isTenantUsable } from "@/tenant/availability";
import { clearAutoRetries } from "@/tenant/retry";
import { applyPwaTitle, linkPwaAssets } from "@/pwa/manifest";
import { watchInstallPrompt } from "@/pwa/install";
import { registerServiceWorker } from "@/pwa/serviceWorker";
import { installPushBridge } from "@/pwa/pushBridge";
import { useSessionStore } from "@/stores/session";
import { captureInviteToken, hasPendingInvite } from "@/auth/invite";
import { fetchIdentityMode, IDENTITY_RETURN_PATH, RESET_PASSWORD_PATH, safeReturnTo } from "@/auth/identity";
import {
  applyPreviewScreen,
  applyPreviewSession,
  previewScreenFrom,
  shouldApplyPreviewSession,
} from "@/dev/previewSession";
import "@/styles/standalone.css";

function hideBootSplash(): void {
  document.getElementById("boot-splash")?.remove();
}

/**
 * The return from a Google / Microsoft / magic-link / verification link.
 * Clear the URL FIRST (same reason as Callback.vue), then exchange the new
 * Better Auth session for a bridge token.
 */
async function finishIdentityReturn(session: ReturnType<typeof useSessionStore>): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  window.history.replaceState(null, "", safeReturnTo(params.get("returnTo")));
  if (error) {
    session.fail(error);
    return;
  }
  await session.completeIdentitySignIn().catch((e) => {
    if (session.status === "signed-out") session.fail(e instanceof Error ? e.message : "Authentication failed");
  });
}

async function boot(): Promise<void> {
  // First, before any await: brand the pre-resolve splash with what THIS host
  // resolved last time (neutral on a first visit — see tenant/splash.ts).
  paintBootSplash(readSplash(window.location.hostname));
  // DEV-1044 — take the invite token off the URL FIRST, before the tenant
  // resolve, the route guard or any redirect can drop the query string. It is
  // parked in sessionStorage and redeemed once the visitor is authenticated.
  captureInviteToken(window.location.pathname, window.location.search);
  const globalState = installGlobalState();
  bindI18nToHost();
  // BOE-51 — point the browser at THIS host's real, same-origin manifest and
  // apple-touch-icon (published per tenant by the backend under /pwa/<host>/)
  // before anything awaits, so installability is evaluated while the tenant
  // is still resolving.
  linkPwaAssets(window.location.hostname);
  // Registered BEFORE anything awaits: `beforeinstallprompt` fires once, on
  // Chromium's own schedule, and cannot be recovered if it is missed.
  watchInstallPrompt();
  // Same reason: a notification tap can reach a tab that is still booting.
  installPushBridge();
  const host = resolveTenantHost();
  // R3: the sign-in mode of this host, read in parallel with the tenant
  // resolve so it adds no wait. Any failure reads as "auth0".
  const identityMode = fetchIdentityMode();

  const app = createApp(App);
  app.use(pinia);
  app.use(i18n);

  let tenant;
  try {
    tenant = await fetchTenantApp(host);
    // Only a REAL answer may rewrite the remembered splash brand — a
    // `found:false` forgets it, but a thrown fetch (offline, 5xx) is not a
    // resolution and must not wipe the brand for the next boot.
    rememberSplash(window.location.hostname, host, tenant);
  } catch {
    // Network/API failure resolving the tenant reads the same as "not found" —
    // Unavailable.vue is the correct terminal state either way.
    tenant = { found: false } as Awaited<ReturnType<typeof fetchTenantApp>>;
  }

  applyBranding(tenant.name || "MetaShip", tenant.branding);
  const vuetify = createShellVuetify(tenant.branding);
  // One theme for chrome AND module: the global state (what the hosted module
  // reads) decides, seeded from the tenant on a first visit.
  vuetify.theme.global.name.value = applyTenantDefaultTheme(globalState, tenant.branding?.defaultTheme);
  app.use(vuetify);

  const devHost = isDevHost(window.location.hostname, import.meta.env.DEV);
  const session = useSessionStore(pinia);
  session.setTenant(host, tenant, devHost);
  session.identityMode = await identityMode;

  app.mount("#app");
  hideBootSplash();

  if (!isTenantUsable(tenant, devHost)) return;
  // The app resolved — a provisioning wait that ended here forgets its
  // automatic-retry count (see tenant/retry.ts).
  clearAutoRetries(window.location.hostname);

  // iOS takes the home-screen title from a meta tag, not the manifest. The
  // manifest itself (name, icons, standalone display — BOE-59) is published by
  // the backend — see pwa/manifest.ts.
  applyPwaTitle(tenant.name || "Metaship");
  // Registered AFTER the resolve above, because the worker carries this
  // tenant's policy in its own script URL (see pwa/serviceWorker.ts).
  void registerServiceWorker({ tenant, host, apiBase: API_BASE_URL });

  if (tenant.importMap.imports.length) {
    // `?mfe=<key>=<url>` — a local module build in place of the CDN one. Dev
    // hosts only, for the same reason as the tenant-host override above it.
    injectImportMap(devHost ? applyDevImportMapOverrides(tenant.importMap.imports) : tenant.importMap.imports);
  }

  // Reload fast-path (persisted, unexpired MetaShip session) → resume silently.
  // Otherwise fall back to a stored Auth0 refresh token; a genuinely fresh
  // visitor lands on Login.vue and waits for an explicit "Sign in" click.
  // The Better Auth return path signs in; the reset page needs no session.
  const bootPath = window.location.pathname;
  if (bootPath === IDENTITY_RETURN_PATH) {
    await finishIdentityReturn(session);
  } else if (
    bootPath !== "/auth0/callback" &&
    bootPath !== RESET_PASSWORD_PATH &&
    !session.resumeFromPersistedSession()
  ) {
    await session.resumeFromRefreshToken().catch(async () => {
      // DEV-1110 — no Auth0 path (no signal, or the refresh token is spent).
      // An ENROLLED device can still open its own app from the last visit's
      // snapshot plus its device credential; anything else falls through to
      // Login.vue for status === "signed-out", as before.
      await session.resumeFromDeviceCredential().catch(() => false);
    });
  }

  // Someone who followed an invite link has already decided; making them read
  // the marketing page and hunt for "Sign in" is a step that only loses them.
  // Sign-up is the right hint: the overwhelming case is a grower with no
  // account, and Auth0's signup screen still offers "log in" to the rest.
  // With the Better Auth form (mode both/identity) the invitee sees the form instead.
  if (session.status === "signed-out" && hasPendingInvite() && session.identityMode === "auth0") {
    await session.beginLogin(undefined, "signup").catch(() => {
      /* fall through to Login.vue */
    });
    return;
  }
  // PAC-1387 — `?previewScreen=verify-email|invite-only|no-access|error` on a
  // dev host: the pre-auth screens that otherwise need a fresh Auth0 account.
  const previewScreen = previewScreenFrom(window.location.search, devHost);
  if (previewScreen) {
    applyPreviewScreen(session, previewScreen);
    return;
  }
  // INS-37 — a programme invite opened in a tab that already has a session
  // never goes through Auth0. Hold the shell on the redeem screen before
  // modules start, so the home redirect cannot drop the token's destination.
  if (session.status === "ready") session.beginProgrammeInvite();
  // After resume attempts — those reset status to signed-out when there is
  // no token. A local `?previewSession=1` must win so chrome can be screenshot.
  if (shouldApplyPreviewSession(window.location.search, devHost)) {
    applyPreviewSession(session);
    // Stay on "/" — a home redirect to /boer would load the production Boer
    // bundle (still English) and strip the preview query.
    return;
  }

  let modulesStarted = false;
  const startModulesOnceReady = () => {
    if (modulesStarted || session.status !== "ready" || !tenant) return;
    const landing = session.takeLandingPath();
    if (landing) {
      try {
        window.history.replaceState(null, "", landing);
      } catch {
        /* the in-app navigation below still aims at it */
      }
    }
    modulesStarted = true;

    registerTenantModules(tenant.modules);
    singleSpa.start();

    window.addEventListener("single-spa:before-routing-event", () => {
      const redirectTo = decideRouteGuard(window.location.pathname, window.location.search, tenant.modules);
      if (redirectTo) singleSpa.navigateToUrl(redirectTo);
    });

    const path = window.location.pathname;
    // Overlays (comms, help-center) match every path by DESIGN — that must
    // NEVER count as "already inside a module" for landing-page purposes, or
    // the presence of an overlay silently disables the home redirect entirely.
    const insideAModule = tenant.modules.some((m) => !m.overlay && routeUnderPrefix(path, m.pathPrefix));
    if (!insideAModule) {
      const home = decideHomeRedirect(tenant.homePath, tenant.modules, !!tenant.homePage?.enabled);
      if (home) singleSpa.navigateToUrl(home);
    }
  };

  watch(() => session.status, startModulesOnceReady, { immediate: true });
}

boot();
